// Parse POS PDF text to extract department sales totals
// PDF format has sections like "Kitchen", "Bakery", "Sandwich", "Soft Drink", "Tea", "Sweets & Chips", "Miscellaneous"
// Each section ends with a subtotal line (starts with whitespace, large number)

const KITCHEN_HEADERS = ['kitchen']
const BAKERY_HEADERS = ['bakery']
const TUCKSHOP_HEADERS = ['sandwich', 'soft drink', 'tea', 'sweets', 'miscellaneous', 'pin', 'cff']

// All recognized section headers
const ALL_SECTION_HEADERS = [...KITCHEN_HEADERS, ...BAKERY_HEADERS, ...TUCKSHOP_HEADERS]

function isHeaderLine(line) {
  const lower = line.trim().toLowerCase()
  return ALL_SECTION_HEADERS.some(h => lower === h || (lower.startsWith(h) && lower.length < h.length + 5))
}

function getSectionType(line) {
  const lower = line.trim().toLowerCase()
  if (KITCHEN_HEADERS.some(h => lower === h || lower.startsWith(h))) return 'kitchen'
  if (BAKERY_HEADERS.some(h => lower === h || lower.startsWith(h))) return 'bakery'
  if (TUCKSHOP_HEADERS.some(h => lower === h || lower.startsWith(h))) return 'tuckShop'
  return null
}

// A subtotal line: starts with whitespace or is mostly numeric, contains a large decimal number
// that doesn't look like a product line (no product name before it, or the line is short)
function extractSubtotal(line) {
  const trimmed = line.trim()
  // Match a number like 1875.00 that could be a subtotal
  // Subtotal lines are typically just a number or have minimal text
  const match = trimmed.match(/^[\s]*(\d[\d,]*\.\d{2})\s/)
  if (match) return parseFloat(match[1].replace(/,/g, ''))
  // Also check for lines that are just a number
  const justNum = trimmed.match(/^(\d[\d,]*\.\d{2})$/)
  if (justNum) return parseFloat(justNum[1].replace(/,/g, ''))
  return null
}

/**
 * Parse POS PDF text to extract department subtotals
 * @param {string} text - Full text extracted from POS PDF
 * @returns {{ kitchen: number, bakery: number, tuckShop: number, grandTotal: number }}
 */
export function parsePdfDepartmentTotals(text) {
  if (!text) return { kitchen: 0, bakery: 0, tuckShop: 0, grandTotal: 0 }

  const lines = text.split('\n')
  const result = { kitchen: 0, bakery: 0, tuckShop: 0, grandTotal: 0 }

  let currentSection = null
  let lastNumberInSection = 0
  const sectionTotals = { kitchen: 0, bakery: 0, tuckShop: 0 }
  let allSectionNumbers = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()
    if (!trimmed) continue

    // Check if this is a section header
    const sectionType = getSectionType(trimmed)
    if (sectionType) {
      // Save the last number from previous section as its subtotal
      if (currentSection && lastNumberInSection > 0) {
        sectionTotals[currentSection] += lastNumberInSection
        allSectionNumbers.push(lastNumberInSection)
      }
      currentSection = sectionType
      lastNumberInSection = 0
      continue
    }

    // Skip non-data lines (headers, page info, etc.)
    if (/^(page\s+\d|group\s+qty|makhado\s+park|for\s+the\s+period|staff\s+pos|sales\s+r\s+cost)/i.test(trimmed)) continue
    if (/^\d{1,2}:\d{2}$/.test(trimmed)) continue

    // Extract numbers from this line
    // POS lines typically have: name qty sales cost% cost total
    // We want the sales figure (second column of numbers, or large numbers)
    const numbers = trimmed.match(/\d[\d,]*\.\d{2}/g)
    if (numbers && numbers.length > 0) {
      // The last large number on the line is most likely the running total or subtotal
      const lastNum = parseFloat(numbers[numbers.length - 1].replace(/,/g, ''))
      // For product lines, the sales figure is typically the 2nd number
      // For subtotal lines (no alphabetic text), it's the main number
      const hasText = /[a-zA-Z]{2,}/.test(trimmed)

      if (!hasText) {
        // This is likely a subtotal or total line
        if (currentSection) {
          lastNumberInSection = lastNum
        }
      } else if (currentSection) {
        // Product line - track the first large number as potential section running total
        // Actually for subtotals we just want the last non-product line number
        // Use the first significant number (sales column)
        if (numbers.length >= 2) {
          const salesNum = parseFloat(numbers[1].replace(/,/g, ''))
          if (salesNum > lastNumberInSection) {
            // Keep track, but the subtotal line will be the definitive one
          }
        }
      }
    }
  }

  // Save the last section's subtotal
  if (currentSection && lastNumberInSection > 0) {
    sectionTotals[currentSection] += lastNumberInSection
    allSectionNumbers.push(lastNumberInSection)
  }

  result.kitchen = sectionTotals.kitchen
  result.bakery = sectionTotals.bakery
  result.tuckShop = sectionTotals.tuckShop

  // Grand total: look for the very last large number in the document
  // It's typically at the bottom after all sections
  let grandTotal = 0
  for (let i = lines.length - 1; i >= 0; i--) {
    const trimmed = lines[i].trim()
    if (!trimmed) continue
    const numbers = trimmed.match(/\d[\d,]*\.\d{2}/g)
    if (numbers) {
      const lastNum = parseFloat(numbers[numbers.length - 1].replace(/,/g, ''))
      // Grand total should be >= sum of sections
      const sectionSum = result.kitchen + result.bakery + result.tuckShop
      if (lastNum >= sectionSum * 0.9 || lastNum > grandTotal) {
        grandTotal = lastNum
        break
      }
    }
  }

  result.grandTotal = grandTotal || (result.kitchen + result.bakery + result.tuckShop)

  return result
}
